import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { Document, Image, Page, Text, View } from "@react-pdf/renderer";

import {
  MediaType,
  ObstacleInput,
  UnitTypes,
  Units,
} from "../../types/sharedTypes";
import { ExtendedTrajectoryData } from "../../services/trajectoryCalculationService";
import pdfDocStyles, {
  chartHeight,
  chartWidth,
  footerHeight,
  headerHeight,
} from "./pdfDocStyles";
import PdfSlider from "./pdfSlider";
import PdfObstacle from "./pdfObstacle";
import PdfHeader from "./pdfHeader";
import PdfGrayDot from "./pdfGrayDot";
import PdfFooter from "./pdfFooter";
import mediumSwitchFoam from "../../assets/pdf/medium-switch-foam.png";
import mediumSwitchWater from "../../assets/pdf/medium-switch-water.png";
import { unitFormatter } from "../../utils/units";

type PdfDocProps = {
  unit: Units;
  monitorName: string;
  monitorDescription: string;
  nozzleName: string;
  nozzleDescription: string;
  angleGuiDataUrl: string;
  chartDataUrl: string;
  selectedAngle: number;
  flowRateMinValue: number;
  flowRateMaxValue: number;
  selectedFlowRate: number;
  pressureMinValue: number;
  pressureMaxValue: number;
  selectedPressure: number;
  monitorHeightMinValue: number;
  monitorHeightMaxValue: number;
  selectedMonitorHeight: number;
  roofObstacles: Array<ObstacleInput>;
  groundObstacles: Array<ObstacleInput>;
  maxNumberOfRoofObstacles: number;
  maxNumberOfGroundObstacles: number;
  output: ExtendedTrajectoryData;
  selectedMedium: MediaType;
};

const PdfDoc: FunctionComponent<PdfDocProps> = ({
  unit,
  monitorName,
  angleGuiDataUrl,
  chartDataUrl,
  monitorDescription,
  nozzleName,
  nozzleDescription,
  selectedAngle,
  flowRateMinValue,
  flowRateMaxValue,
  selectedFlowRate,
  pressureMinValue,
  pressureMaxValue,
  selectedPressure,
  monitorHeightMinValue,
  monitorHeightMaxValue,
  selectedMonitorHeight,
  roofObstacles,
  groundObstacles,
  maxNumberOfRoofObstacles,
  maxNumberOfGroundObstacles,
  output,
  selectedMedium,
}) => {
  const { t } = useTranslation();

  const flowRatePercent =
    ((selectedFlowRate - flowRateMinValue) * 100) /
    (flowRateMaxValue - flowRateMinValue);
  const pressurePercent =
    ((selectedPressure - pressureMinValue) * 100) /
    (pressureMaxValue - pressureMinValue);
  const monitorHeightPercent =
    ((selectedMonitorHeight - monitorHeightMinValue) * 100) /
    (monitorHeightMaxValue - monitorHeightMinValue);
  const now = new Date();

  return (
    <Document>
      <Page size="A4" style={pdfDocStyles.page}>
        <PdfHeader height={headerHeight} />

        <View
          style={{
            marginTop: 5,
            marginLeft: 20,
            marginRight: 20,
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
          }}
        >
          <View style={{ flexGrow: 1, position: "relative" }}>
            <View
              style={{
                position: "absolute",
                top: 3,
                left: 0,
                width: 10,
                height: 10,
                backgroundColor: "#ff0000",
              }}
            />
            <View style={{ position: "relative", left: 15 }}>
              <Text style={pdfDocStyles.redDotLabel}>
                {t("components.settings.title")}
              </Text>
            </View>
          </View>

          <View style={{ flexGrow: 1 }}>
            <Text
              style={{ ...pdfDocStyles.redDotLabel, alignSelf: "flex-end" }}
            >
              {t("components.pdfExport.createdAt", {
                dd: now.getDate().toString().padStart(2, "0"),
                mm: (now.getMonth() + 1).toString().padStart(2, "0"),
                yyyy: now.getFullYear(),
              })}
            </Text>
          </View>
        </View>

        <View
          style={{
            flexDirection: "row",
            marginTop: 5,
            marginLeft: 15,
            marginRight: 15,
          }}
        >
          <View
            style={{
              flexGrow: 1,
              border: "3px solid #e8e8e8",
              marginRight: 5,
              padding: 5,
              maxWidth: 300,
            }}
          >
            <PdfGrayDot
              label={t("components.settings.monitorOption", { monitorName })}
              withBorderBottom
              toUpperCase
            />

            <Text style={{ fontSize: 10 }}>{monitorDescription}</Text>

            <View style={{ marginTop: 5 }}>
              <PdfGrayDot
                label={t("components.settings.nozzleOption", { nozzleName })}
                withBorderBottom
                toUpperCase
              />
            </View>

            <Text style={{ fontSize: 10 }}>{nozzleDescription}</Text>

            <View style={{ marginTop: 5 }}>
              <PdfGrayDot
                label={`${t(
                  "components.settings.headlineAngle"
                )}: ${selectedAngle}°`}
                withBorderBottom
                toUpperCase
              />
              <View
                style={{ flexDirection: "row", justifyContent: "space-around" }}
              >
                <Image
                  src={angleGuiDataUrl}
                  style={{ width: 200, height: 200 }}
                />
              </View>
            </View>
          </View>

          <View
            style={{
              flexGrow: 1,
              border: "3px solid #e8e8e8",
              marginLeft: 5,
              padding: 10,
            }}
          >
            <PdfGrayDot
              label={t("components.settings.headlineMedium")}
              withBorderBottom
              toUpperCase
            />

            <View style={{ marginBottom: 5, flexDirection: "row" }}>
              <View
                style={{
                  flexDirection: "column",
                  justifyContent: "space-around",
                }}
              >
                <Text style={{ fontSize: 10, fontWeight: "bold" }}>
                  {t("components.settings.mediumWater")}
                </Text>
              </View>
              <View
                style={{
                  flexDirection: "column",
                  justifyContent: "space-around",
                  marginLeft: 10,
                  marginRight: 10,
                }}
              >
                <Image
                  src={
                    selectedMedium === "water"
                      ? mediumSwitchWater
                      : mediumSwitchFoam
                  }
                  style={{ width: 33, height: 21 }}
                />
              </View>
              <View
                style={{
                  flexDirection: "column",
                  justifyContent: "space-around",
                }}
              >
                <Text style={{ fontSize: 10, fontWeight: "bold" }}>
                  {t("components.settings.mediumFoam")}
                </Text>
              </View>
            </View>

            <View style={{ marginBottom: 5 }}>
              <PdfSlider
                percent={flowRatePercent}
                label={t("components.settings.headlineExtinguishingAgentFlow")}
                value={`${unitFormatter(
                  selectedFlowRate,
                  UnitTypes.Liter,
                  unit
                )} ${t(
                  unit === Units.Metric
                    ? "units.literPerMinute"
                    : "units.gallonPerMinute"
                )}`}
              />
            </View>

            <View style={{ marginBottom: 5 }}>
              <PdfSlider
                percent={pressurePercent}
                label={t("components.settings.headlinePressure")}
                value={`${unitFormatter(
                  selectedPressure,
                  UnitTypes.Bar,
                  unit
                )} ${t(t(unit === Units.Metric ? "units.bar" : "units.psi"))}`}
              />
            </View>

            <View style={{ marginBottom: 5 }}>
              <PdfSlider
                percent={monitorHeightPercent}
                label={t("components.settings.headlineMonitorHeight")}
                value={`${unitFormatter(
                  selectedMonitorHeight,
                  UnitTypes.Meter,
                  unit
                )} ${t(
                  t(unit === Units.Metric ? "units.meter" : "units.foot")
                )}`}
              />
            </View>
            {roofObstacles.length > 0 && (
              <View>
                <PdfGrayDot
                  label={t("components.settings.headlineRoofObstacle")}
                  toUpperCase
                  withBorderBottom
                />
                {roofObstacles.map((obstacle, index) => (
                  <View key={index} style={{ marginBottom: 5 }}>
                    <PdfObstacle
                      unit={unit}
                      obstacle={obstacle}
                      obstacleNumber={
                        maxNumberOfRoofObstacles > 1 ? index + 1 : null
                      }
                    />
                  </View>
                ))}
              </View>
            )}
            {groundObstacles.length > 0 && (
              <View>
                <PdfGrayDot
                  label={t("components.settings.headlineGroundObstacle")}
                  toUpperCase
                  withBorderBottom
                />
                {groundObstacles.map((obstacle, index) => (
                  <View key={index} style={{ marginBottom: 5 }}>
                    <PdfObstacle
                      unit={unit}
                      obstacle={obstacle}
                      obstacleNumber={
                        maxNumberOfGroundObstacles > 1 ? index + 1 : null
                      }
                    />
                  </View>
                ))}
              </View>
            )}
          </View>
        </View>

        <View wrap={false}>
          <View style={{ marginTop: 15, marginLeft: 20, marginRight: 20 }}>
            <View style={{ flexGrow: 1, position: "relative" }}>
              <View
                style={{
                  position: "absolute",
                  top: 3,
                  left: 0,
                  width: 10,
                  height: 10,
                  backgroundColor: "#ff0000",
                }}
              />
              <View style={{ position: "relative", left: 15 }}>
                <Text style={{ fontSize: 12, fontWeight: "bold" }}>
                  {t("components.results.title")}
                </Text>
              </View>
            </View>
          </View>

          <View
            style={{
              marginTop: 10,
              marginLeft: 15,
              marginRight: 15,
              border: "3px solid #e8e8e8",
              padding: 10,
              display: "flex",
            }}
          >
            <View style={{ flexDirection: "row" }}>
              <View style={{ color: "#4A4A4A", paddingRight: 50 }}>
                <Text style={{ fontWeight: "bold" }}>
                  {t("formatting.numberMaxOneDecimalPoint", {
                    number: unitFormatter(output.range, UnitTypes.Meter, unit),
                  })}{" "}
                  {t(unit === Units.Metric ? "units.meter" : "units.foot")}
                </Text>
                <PdfGrayDot
                  label={t("components.resultsTextualSummary.range")}
                />

                <Text style={{ fontWeight: "bold" }}>
                  {t("formatting.numberMaxOneDecimalPoint", {
                    number: unitFormatter(
                      output.highestPoint.y,
                      UnitTypes.Meter,
                      unit
                    ),
                  })}{" "}
                  {t(unit === Units.Metric ? "units.meter" : "units.foot")}
                </Text>
                <PdfGrayDot
                  label={t("components.resultsTextualSummary.throwingHeight")}
                />

                <Text style={{ fontWeight: "bold" }}>
                  {t("formatting.numberMaxOneDecimalPoint", {
                    number: unitFormatter(
                      output.highestPoint.x,
                      UnitTypes.Meter,
                      unit
                    ),
                  })}{" "}
                  {t(unit === Units.Metric ? "units.meter" : "units.foot")}
                </Text>
                <PdfGrayDot
                  label={t(
                    "components.resultsTextualSummary.distanceOfHeighestPoint"
                  )}
                />

                <Text style={{ fontWeight: "bold" }}>
                  {t("formatting.numberMaxOneDecimalPoint", {
                    number: output.recoilInNewton / 1000,
                  })}{" "}
                  {t("units.kiloNewton")}
                </Text>
                <PdfGrayDot
                  label={t("components.resultsTextualSummary.recoil")}
                />

                <Text style={{ fontWeight: "bold" }}>
                  {t("formatting.numberMaxOneDecimalPoint", {
                    number: unitFormatter(
                      output.impactAreaDiameter,
                      UnitTypes.Meter,
                      unit
                    ),
                  })}{" "}
                  {t(unit === Units.Metric ? "units.meter" : "units.foot")}
                </Text>
                <PdfGrayDot
                  label={t(
                    "components.resultsTextualSummary.footprintDiameter"
                  )}
                />
              </View>
              <View>
                <Image
                  src={chartDataUrl}
                  style={{ width: chartWidth, height: chartHeight }}
                />
              </View>
            </View>
            <Text style={{ fontSize: 10 }}>
              {t("components.results.footerText")}
            </Text>
          </View>
        </View>

        <PdfFooter height={footerHeight} />
      </Page>
    </Document>
  );
};

export default PdfDoc;
